// Modal pra adicionar uma URL pública como fonte do caderno.
// Backend faz o fetch + extrai texto legível (Readability light) e salva
// como aiStudyDocument com sourceKind='url'.
//
// [[project-caderno-notebooklm-roadmap]] Fase 1.

import { useEffect, useState } from "preact/hooks";
import { Globe, Loader2, X } from "lucide-react";
import { api } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";

export function IngestUrlModal({
  sessionId,
  onClose,
  onIngested
}: {
  sessionId: string;
  onClose: () => void;
  onIngested: () => void;
}) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, busy]);

  async function submit(ev?: Event) {
    ev?.preventDefault();
    const trimmed = url.trim();
    if (!trimmed) {
      setErr("Cole uma URL.");
      return;
    }
    if (!/^https?:\/\//i.test(trimmed)) {
      setErr("URL precisa começar com http:// ou https://.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await api.ingestAiStudyUrl(sessionId, trimmed);
      onIngested();
      onClose();
    } catch (e) {
      setErr(friendlyError(e));
      setBusy(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-3"
      role="dialog"
      aria-modal="true"
      aria-label="Adicionar URL como fonte"
    >
      <form
        onSubmit={submit}
        class="flex w-full max-w-md flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-2xl"
      >
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-2">
            <Globe className="size-5 text-sky-500" />
            <div class="text-base font-bold">Adicionar URL como fonte</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            class="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent disabled:opacity-40"
            aria-label="Fechar"
          >
            <X className="size-4" />
          </button>
        </div>
        <div class="text-xs text-muted-foreground">
          Cole o link de um artigo, página web ou material online. A IA vai
          extrair o texto legível e usar como contexto do caderno.
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            URL
          </label>
          <input
            type="url"
            value={url}
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
            placeholder="https://exemplo.com/artigo"
            disabled={busy}
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-sky-500/50 focus:outline-none disabled:opacity-50"
            autofocus
            spellcheck={false}
          />
        </div>
        {err && (
          <div class="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {err}
          </div>
        )}
        <div class="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy || !url.trim()}
            class="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {busy ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                Buscando…
              </>
            ) : (
              <>
                <Globe className="size-3.5" />
                Adicionar fonte
              </>
            )}
          </button>
        </div>
        <div class="text-[10px] text-muted-foreground">
          Buscamos a página (~até 15s). Só páginas públicas HTML — PDFs e
          conteúdos atrás de login não funcionam.
        </div>
      </form>
    </div>
  );
}
